import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import type { AccountRecord } from '@/crypto/vault';
import { SessionProvider, useIdleLock } from '@/session/SessionContext';
import { migrateLegacyAccounts } from '@/db/migrateAccounts';
import { LoginScreen } from '@/features/auth/LoginScreen';
import { SetupScreen } from '@/features/auth/SetupScreen';
import { WelcomeScreen } from '@/features/auth/WelcomeScreen';
import { AppLayout } from '@/features/shell/AppLayout';
import { DashboardScreen } from '@/features/dashboard/DashboardScreen';
import { TripListScreen } from '@/features/trips/TripListScreen';
import { TripFormScreen } from '@/features/trips/TripFormScreen';
import { TripDetailScreen } from '@/features/trips/TripDetailScreen';
import { OrderListScreen } from '@/features/orders/OrderListScreen';
import { ShiftScreen } from '@/features/shifts/ShiftScreen';
import { InvoiceListScreen } from '@/features/invoices/InvoiceListScreen';
import { InvoiceFormScreen } from '@/features/invoices/InvoiceFormScreen';
import { InvoiceDetailScreen } from '@/features/invoices/InvoiceDetailScreen';
import { ExportScreen } from '@/features/exports/ExportScreen';
import { BackupScreen } from '@/features/backup/BackupScreen';
import { SettingsScreen } from '@/features/settings/SettingsScreen';
import { useT } from '@/i18n';

interface Unlocked {
  account: AccountRecord;
  dataKey: CryptoKey;
}

export function App() {
  const t = useT();
  const [unlocked, setUnlocked] = useState<Unlocked | null>(null);
  const [migrated, setMigrated] = useState(false);
  /** Which of the locked screens is showing. Reset to the choice on lock. */
  const [gate, setGate] = useState<'welcome' | 'login' | 'register'>('welcome');

  useEffect(() => {
    void (async () => {
      // Accounts used to live alongside the books; move them before anything
      // reads the registry.
      await migrateLegacyAccounts();
      setMigrated(true);
    })();
  }, []);

  const lock = useCallback(() => {
    setUnlocked(null);
    setGate('welcome');
  }, []);
  useIdleLock(lock);

  if (!migrated) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-muted text-sm">{t.common.loading}</p>
      </div>
    );
  }

  if (!unlocked) {
    // Both routes are offered up front, on every device and every launch,
    // rather than one being chosen for the driver by whether an account
    // happens to exist.
    if (gate === 'welcome') {
      return (
        <WelcomeScreen
          onSignIn={() => setGate('login')}
          onRegister={() => setGate('register')}
        />
      );
    }

    return gate === 'register' ? (
      <SetupScreen
        onCancel={() => setGate('welcome')}
        onCreated={(created) =>
          setUnlocked({ account: created.account, dataKey: created.dataKey })
        }
      />
    ) : (
      <LoginScreen onUnlocked={setUnlocked} onBack={() => setGate('welcome')} />
    );
  }

  return (
    <SessionProvider unlocked={unlocked} onLock={lock}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<DashboardScreen />} />
          <Route path="fahrten" element={<TripListScreen />} />
          <Route path="fahrten/neu" element={<TripFormScreen />} />
          <Route path="fahrten/:tripId" element={<TripDetailScreen />} />
          <Route path="fahrten/:tripId/bearbeiten" element={<TripFormScreen />} />
          <Route path="auftraege" element={<OrderListScreen />} />
          <Route path="schichten" element={<ShiftScreen />} />
          <Route path="rechnungen" element={<InvoiceListScreen />} />
          <Route path="rechnungen/neu" element={<InvoiceFormScreen />} />
          <Route path="rechnungen/:invoiceId" element={<InvoiceDetailScreen />} />
          <Route path="rechnungen/:invoiceId/bearbeiten" element={<InvoiceFormScreen />} />
          <Route path="export" element={<ExportScreen />} />
          <Route path="sicherung" element={<BackupScreen />} />
          <Route path="einstellungen" element={<SettingsScreen />} />
          <Route path="*" element={<DashboardScreen />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}
